import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleRounded } from '@mui/icons-material';
import { ThemeProvider } from '@mui/material/styles';
import { TeachMark } from '../../components/TeachMark';
import { onboardingApi } from '../../lib/api/onboarding.api';
import { authTheme } from '../auth/auth-theme';
import { useFunnelLanguage } from '../auth/use-funnel-language';

const EMERALD = '#0f766e';
const ACCENT = '#10b981';

function slugify(input: string): string {
  const slug = input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return slug;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function RegisterSeminaryPage() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { language, switchLanguage } = useFunnelLanguage();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const [seminaryName, setSeminaryName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [country, setCountry] = useState('');
  const [estimatedSize, setEstimatedSize] = useState('');
  const [message, setMessage] = useState('');

  // auto-derive the slug from the name until the user edits it manually
  function onNameChange(value: string) {
    setSeminaryName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  const stepLabels = [
    t('register.steps.identity'),
    t('register.steps.details'),
    t('register.steps.confirm'),
  ];

  const identityValid = useMemo(
    () =>
      seminaryName.trim().length >= 2 &&
      /^[a-z0-9-]+$/.test(slug) &&
      contactName.trim().length >= 2 &&
      EMAIL_RE.test(contactEmail),
    [seminaryName, slug, contactName, contactEmail],
  );

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      await onboardingApi.submit({
        seminaryName: seminaryName.trim(),
        suggestedSlug: slug,
        contactName: contactName.trim(),
        contactEmail: contactEmail.trim(),
        ...(contactPhone.trim() ? { contactPhone: contactPhone.trim() } : {}),
        ...(country.trim() ? { country: country.trim() } : {}),
        ...(estimatedSize ? { estimatedSize } : {}),
        ...(message.trim() ? { message: message.trim() } : {}),
      });
      setDone(true);
    } catch {
      setError(t('register.errors.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  const sizeOptions = [
    { value: 'small', label: t('register.sizes.small') },
    { value: 'medium', label: t('register.sizes.medium') },
    { value: 'large', label: t('register.sizes.large') },
    { value: 'xlarge', label: t('register.sizes.xlarge') },
  ];

  return (
    <ThemeProvider theme={authTheme}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          background: 'linear-gradient(135deg, #f6f4ee 0%, #eaf3ef 55%, #e2efe9 100%)',
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ position: 'absolute', top: 20, right: 24 }}
          role="group"
        >
          {(['es', 'en'] as const).map((lng) => (
            <Button
              key={lng}
              size="small"
              onClick={() => switchLanguage(lng)}
              sx={{
                minWidth: 40,
                fontWeight: 700,
                fontSize: 12,
                color: language === lng ? EMERALD : 'rgba(19,32,29,0.5)',
                background: language === lng ? 'rgba(15,118,110,0.1)' : 'transparent',
              }}
            >
              {lng.toUpperCase()}
            </Button>
          ))}
        </Stack>

        <Card
          sx={{
            width: 560,
            maxWidth: '100%',
            borderRadius: 4,
            border: '1px solid rgba(19,32,29,0.08)',
            boxShadow: '0 24px 60px -24px rgba(19,32,29,0.28)',
            background: '#fffdf8',
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4.5 } }}>
            {done ? (
              <Stack alignItems="center" spacing={2} textAlign="center" py={2}>
                <CheckCircleRounded sx={{ fontSize: 56, color: EMERALD }} />
                <Typography variant="h5" fontWeight={800} sx={{ color: '#13201d' }}>
                  {t('register.successTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(19,32,29,0.6)', maxWidth: 380 }}>
                  {t('register.successBody', { email: contactEmail })}
                </Typography>
                <Button
                  onClick={() => navigate('/login')}
                  variant="contained"
                  sx={{
                    mt: 1,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2.5,
                    background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                  }}
                >
                  {t('register.backToLogin')}
                </Button>
              </Stack>
            ) : (
              <>
                <Stack alignItems="center" spacing={1} mb={3}>
                  <TeachMark size={50} title="Teach by DosFilos" />
                  <Typography variant="h5" fontWeight={800} sx={{ color: '#13201d' }}>
                    {t('register.title')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: 'rgba(19,32,29,0.55)', textAlign: 'center' }}
                  >
                    {t('register.subtitle')}
                  </Typography>
                </Stack>

                <Stepper activeStep={step} sx={{ mb: 3 }}>
                  {stepLabels.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {step === 0 && (
                  <Stack spacing={2}>
                    <TextField
                      label={t('register.fields.seminaryName')}
                      value={seminaryName}
                      onChange={(e) => onNameChange(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label={t('register.fields.slug')}
                      value={slug}
                      onChange={(e) => {
                        setSlugTouched(true);
                        setSlug(slugify(e.target.value));
                      }}
                      helperText={t('register.fields.slugHelp')}
                      required
                      fullWidth
                    />
                    <TextField
                      label={t('register.fields.contactName')}
                      value={contactName}
                      onChange={(e) => setContactName(e.target.value)}
                      required
                      fullWidth
                    />
                    <TextField
                      label={t('register.fields.contactEmail')}
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      required
                      fullWidth
                    />
                  </Stack>
                )}

                {step === 1 && (
                  <Stack spacing={2}>
                    <TextField
                      label={t('register.fields.contactPhone')}
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label={t('register.fields.country')}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      select
                      label={t('register.fields.estimatedSize')}
                      value={estimatedSize}
                      onChange={(e) => setEstimatedSize(e.target.value)}
                      fullWidth
                    >
                      <MenuItem value="">{t('register.sizes.placeholder')}</MenuItem>
                      {sizeOptions.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={t('register.fields.message')}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      multiline
                      minRows={3}
                      fullWidth
                    />
                  </Stack>
                )}

                {step === 2 && (
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#13201d' }}>
                      {t('register.confirmTitle')}
                    </Typography>
                    {[
                      [t('register.fields.seminaryName'), seminaryName],
                      [t('register.fields.slug'), slug],
                      [t('register.fields.contactName'), contactName],
                      [t('register.fields.contactEmail'), contactEmail],
                      [t('register.fields.contactPhone'), contactPhone || '—'],
                      [t('register.fields.country'), country || '—'],
                    ].map(([label, value]) => (
                      <Stack
                        key={label}
                        direction="row"
                        justifyContent="space-between"
                        sx={{ fontSize: 14 }}
                      >
                        <span style={{ color: 'rgba(19,32,29,0.55)' }}>{label}</span>
                        <span style={{ color: '#13201d', fontWeight: 600, textAlign: 'right' }}>
                          {value}
                        </span>
                      </Stack>
                    ))}
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                  <Button
                    onClick={() => (step === 0 ? navigate('/login') : setStep(step - 1))}
                    sx={{ textTransform: 'none', color: 'rgba(19,32,29,0.6)' }}
                  >
                    {step === 0 ? t('register.backToLogin') : t('register.back')}
                  </Button>
                  {step < 2 ? (
                    <Button
                      variant="contained"
                      disabled={step === 0 && !identityValid}
                      onClick={() => setStep(step + 1)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2.5,
                        px: 3,
                        background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                      }}
                    >
                      {t('register.next')}
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={submitting || !identityValid}
                      onClick={handleSubmit}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2.5,
                        px: 3,
                        background: `linear-gradient(135deg, ${EMERALD} 0%, ${ACCENT} 100%)`,
                      }}
                    >
                      {submitting ? (
                        <CircularProgress size={22} color="inherit" />
                      ) : (
                        t('register.submit')
                      )}
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
}
